import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router";
import { Form, FormField, FormItem, FormMessage } from "@/components/ui/form";
import { useForm } from "react-hook-form";
import { step4Schema } from "@/schemas/onboarding-schema";
import { z } from "zod";
import { Loader2 } from "lucide-react";
import { useOnboarding } from "@/hooks/useOnboarding";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useCallback, useMemo, useEffect, memo, useRef } from "react";
import { useWatch } from "react-hook-form";
import { DateSelector } from "./DateSelector";
import { DaySchedule } from "./DaySchedule";
import { DAYS } from "./types";
import { type DateSelectorRef } from "./types";
import { zodResolver } from "@hookform/resolvers/zod";
import { OrgSelection } from "@/components/org-selection";
import FormShell from "@/components/form-shell";
import { useRightPanel } from "@/hooks/useRightPanel";

export const OnboardingStep4 = memo(function OnboardingStep4() {
  const {
    organizations,
    showOrgSelection,
    selectedOrgId,
    setSelectedOrgId,
    isSettingActive,
    handleSetActiveOrganization,
    onCloseOrgSelection,
    updateOnboardingStep,
  } = useOnboarding("step4");
  const navigate = useNavigate();
  const { setRight } = useRightPanel();
  const dateSelectorRef = useRef<DateSelectorRef>(null);

  // Mutation pour sauvegarder les horaires d'ouverture
  const { mutate: saveOpeningHours, isPending: isLoading } = api.useMutation("post", "/onboarding/opening", {
    onSuccess: async () => {
      toast.success("Horaires d'ouverture sauvegardés avec succès!");
      await updateOnboardingStep("step6");
      navigate("/onboarding/step6");
    },
    onError: (error) => {
      console.error("Error saving opening hours:", error);
      toast.error("Erreur lors de la sauvegarde", {
        description: error.message || "Impossible de sauvegarder les horaires d'ouverture",
      });
    },
  });

  useEffect(() => {
    setRight(<div className="flex h-full flex-1 items-center justify-center overflow-hidden p-6 text-6xl font-bold text-black">Nautika</div>);
  }, [setRight]);

  const form = useForm<z.input<typeof step4Schema>>({
    resolver: zodResolver(step4Schema),
    mode: "onChange",
    defaultValues: {
      startDate: "",
      endDate: "",
      openingDays: [],
      openingHours: {
        lundi: {
          matin: { active: true, start: "-", end: "-", maxChildren: undefined },
          midi: { active: false, start: "-", end: "-", maxChildren: undefined },
          apres: { active: true, start: "-", end: "-", maxChildren: undefined },
        },
        mardi: {
          matin: { active: true, start: "-", end: "-", maxChildren: undefined },
          midi: { active: false, start: "-", end: "-", maxChildren: undefined },
          apres: { active: true, start: "-", end: "-", maxChildren: undefined },
        },
        mercredi: {
          matin: { active: true, start: "-", end: "-", maxChildren: undefined },
          midi: { active: false, start: "-", end: "-", maxChildren: undefined },
          apres: { active: true, start: "-", end: "-", maxChildren: undefined },
        },
        jeudi: {
          matin: { active: true, start: "-", end: "-", maxChildren: undefined },
          midi: { active: false, start: "-", end: "-", maxChildren: undefined },
          apres: { active: true, start: "-", end: "-", maxChildren: undefined },
        },
        vendredi: {
          matin: { active: true, start: "-", end: "-", maxChildren: undefined },
          midi: { active: false, start: "-", end: "-", maxChildren: undefined },
          apres: { active: true, start: "-", end: "-", maxChildren: undefined },
        },
        samedi: {
          matin: { active: true, start: "-", end: "-", maxChildren: undefined },
          midi: { active: false, start: "-", end: "-", maxChildren: undefined },
          apres: { active: true, start: "-", end: "-", maxChildren: undefined },
        },
        dimanche: {
          matin: { active: true, start: "-", end: "-", maxChildren: undefined },
          midi: { active: false, start: "-", end: "-", maxChildren: undefined },
          apres: { active: true, start: "-", end: "-", maxChildren: undefined },
        },
      },
    },
  });

  const onSubmit = useCallback(
    async (data: z.input<typeof step4Schema>) => {
      // After resolver, data is actually parsed; cast to the output type for clarity
      const parsed = data as z.infer<typeof step4Schema>;
      // Filter opening hours to only include periods for selected days
      const filteredOpeningHours = { ...parsed.openingHours };

      // For each day not in openingDays, set all periods to inactive
      Object.keys(filteredOpeningHours).forEach((day) => {
        if (!parsed.openingDays.includes(day)) {
          filteredOpeningHours[day as keyof typeof filteredOpeningHours] = {
            matin: { active: false, start: "-", end: "-", maxChildren: 0 },
            midi: { active: false, start: "-", end: "-", maxChildren: 0 },
            apres: { active: false, start: "-", end: "-", maxChildren: 0 },
          };
        }
      });

      // Convertir les dates DD/MM/YYYY vers UTC ISO string
      const parseDateString = (dateStr: string): string => {
        const [day, month, year] = dateStr.split("/").map(Number);
        // Créer une date locale (midnight dans la timezone de l'utilisateur)
        return new Date(year, month - 1, day, 0, 0, 0, 0).toUTCString();
      };

      saveOpeningHours({
        body: {
          startDate: parseDateString(parsed.startDate),
          endDate: parseDateString(parsed.endDate),
          openingDays: parsed.openingDays,
          openingHours: filteredOpeningHours,
        },
      });
    },
    [saveOpeningHours]
  );

  // Handle form submission with validation error handling
  const handleFormSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      form.handleSubmit(onSubmit, (errors) => {
        // Check if there are date validation errors
        if (errors.startDate || errors.endDate) {
          // Scroll to date selector if there are date errors
          dateSelectorRef.current?.scrollIntoView();
        }
      })();
    },
    [form, onSubmit]
  );

  const handleStartDateChange = useCallback(
    (value: string) => {
      form.setValue("startDate", value, { shouldValidate: true, shouldDirty: true });
    },
    [form]
  );

  const handleEndDateChange = useCallback(
    (value: string) => {
      form.setValue("endDate", value, { shouldValidate: true, shouldDirty: true });
    },
    [form]
  );

  // Use useWatch for efficient form value watching
  const startDate = useWatch({
    control: form.control,
    name: "startDate",
  });

  const endDate = useWatch({
    control: form.control,
    name: "endDate",
  });

  // Memoize the days list to prevent unnecessary re-renders
  const memoizedDays = useMemo(() => DAYS, []);

  // Memoize DateSelector props
  const dateSelectorProps = useMemo(
    () => ({
      startDateValue: startDate || "",
      endDateValue: endDate || "",
      onStartDateChange: handleStartDateChange,
      onEndDateChange: handleEndDateChange,
    }),
    [startDate, endDate, handleStartDateChange, handleEndDateChange]
  );

  return (
    <>
      <FormShell
        title="Périodes d'ouverture"
        subtitle="Configurez les dates de début et de fin de votre club et configurez les périodes disponibles"
        onSubmit={handleFormSubmit}
      >
        <Form {...form}>
          <div className="space-y-8">
            <DateSelector ref={dateSelectorRef} {...dateSelectorProps} />

            <hr className="my-10 border-gray-200" />

            <div className="flex flex-col gap-y-6">
              {memoizedDays.map((day) => (
                <DaySchedule key={day.id} day={day} />
              ))}

              {/* Message d'erreur pour la sélection des jours */}
              <FormField
                control={form.control}
                name="openingDays"
                render={() => (
                  <FormItem>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Message d'erreur pour les horaires d'ouverture */}
              <FormField
                control={form.control}
                name="openingHours"
                render={() => (
                  <FormItem>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="pt-8">
              <Button type="submit" className="flex h-12 w-full items-center justify-center text-base font-medium" disabled={isLoading}>
                {isLoading ? <Loader2 className="mr-2 h-5 w-5 animate-spin" /> : "Continuer"}
              </Button>
            </div>
          </div>
        </Form>
      </FormShell>

      <OrgSelection
        open={showOrgSelection}
        organizations={organizations || []}
        selectedOrgId={selectedOrgId}
        onSelectedOrgChange={setSelectedOrgId}
        onConfirm={() => handleSetActiveOrganization(selectedOrgId)}
        onClose={onCloseOrgSelection}
        isLoading={isSettingActive}
      />
    </>
  );
});
